import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatDate } from "../lib/api";
import { toast } from "sonner";
import { formatApiError } from "../lib/errors";
import { Wallet, TrendingUp, Banknote, Activity, Layers, CalendarRange, AlertTriangle, HelpCircle, ShieldCheck, ArrowRight } from "lucide-react";

const fmtMoney = (n) => n == null ? "—" :
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const fmtPct = (n) => n == null ? "—" : `${(n * 100).toFixed(2)}%`;
const fmtNum = (n) => n == null ? "—" : new Intl.NumberFormat("en-US").format(n);

const CATEGORY = {
  contractual: { bg: "#EFF6FF", border: "#BFDBFE", label: "Contractual", color: "#1E40AF" },
  realized:    { bg: "#ECFDF5", border: "#A7F3D0", label: "Realized",    color: "#065F46" },
  forecast:    { bg: "#FEF3C7", border: "#FCD34D", label: "Forecast",    color: "#92400E" },
};

const _todayIso = () => new Date().toISOString().slice(0, 10);

export default function FinancePortfolio() {
  const [asOf, setAsOf] = useState(_todayIso());
  const [data, setData] = useState(null);
  const [dq, setDq] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: portfolio }, { data: quality }] = await Promise.all([
        api.get("/finance/v2/portfolio", { params: { as_of: asOf }}),
        api.get("/finance/v2/data-quality"),
      ]);
      setData(portfolio);
      setDq(quality);
    } catch (e) {
      toast.error("Load failed: " + formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [asOf]);

  if (loading && !data) return <div className="p-10 text-[#6B7280]" data-testid="finance-portfolio-loading">Loading Finance V2…</div>;
  if (!data) return null;

  const c = data.contractual; const r = data.realized; const cnt = data.counts;
  const hasRealized = cnt.realized_simple_cohort > 0;

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="finance-portfolio">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Finance V2 · preview</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#0A2540]">Portfolio Overview</h1>
          <div className="text-sm text-[#6B7280] mt-1.5">
            Single source of truth for portfolio economics. All metrics categorized as <CategoryChip category="contractual" />, <CategoryChip category="realized" />, or <CategoryChip category="forecast" />.
          </div>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#6B7280] block mb-1">As of</label>
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
                   data-testid="finance-portfolio-as-of"
                   className="h-9 px-3 text-sm border border-[#E5E7EB] rounded-md bg-white" />
          </div>
        </div>
      </div>

      {/* Category legend banner */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white p-3 mb-6 text-xs text-[#6B7280] flex flex-wrap items-center gap-x-4 gap-y-1">
        <span><CategoryChip category="contractual" /> — accrued position based on loan contract terms</span>
        <span><CategoryChip category="realized" /> — actual recorded cash outcomes on closed loans</span>
        <span><CategoryChip category="forecast" /> — modeled projections (Pro Forma — coming)</span>
      </div>

      {/* Counts row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <CountCard label="Total loans" value={cnt.loans_total} testid="count-total" />
        <CountCard label="Open" value={cnt.open} testid="count-open" />
        <CountCard label="Settled / closed" value={cnt.settled_closed} testid="count-settled" />
        <CountCard label="Simple realized cohort" value={cnt.realized_simple_cohort} accent testid="count-simple-realized" />
        <CountCard label="Unresolved settlements" value={cnt.unresolved_realized_settlements} warn testid="count-unresolved" />
      </div>

      {/* Data Quality card */}
      {dq && <DataQualityCard dq={dq} />}

      {/* Contractual section */}
      <Section title="Contractual position" category="contractual">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={Wallet}    label="Deployed capital (cumulative)"     value={fmtMoney(c.deployed_capital_cumulative)} testid="kpi-deployed-cumulative" />
          <Kpi icon={Wallet}    label="Deployed — open book"              value={fmtMoney(c.deployed_capital_open_book)} testid="kpi-deployed-open" />
          <Kpi icon={Banknote}  label="Contractual receivable (open)"     value={fmtMoney(c.contractual_receivable_open)} accent testid="kpi-receivable-open" />
          <Kpi icon={TrendingUp} label="Accrued unrealized return"        value={fmtMoney(c.accrued_unrealized_return)} testid="kpi-accrued-unrealized" />

          <Kpi icon={Layers}    label="Outstanding principal (open)"      value={fmtMoney(c.outstanding_principal_open)} testid="kpi-outstanding-principal" />
          <Kpi icon={Layers}    label="Gross receivable (open)"           value={fmtMoney(c.gross_contractual_receivable_open)}
                sub="Before paydowns + reductions" testid="kpi-gross-receivable" />
          <Kpi icon={CalendarRange} label="Wtd. avg months outstanding"   value={c.weighted_avg_months_outstanding.toFixed(1)} testid="kpi-wa-months" />
          <Kpi icon={Activity}  label="Wtd. avg monthly rate"             value={`${(c.weighted_avg_monthly_rate * 100).toFixed(2)}%`}
                sub="Principal-weighted, open book only" testid="kpi-wa-rate" />
        </div>
      </Section>

      {/* Realized section */}
      <Section title="Realized outcomes" category="realized"
               subtitle={hasRealized
                 ? `${cnt.realized_simple_cohort} loan${cnt.realized_simple_cohort === 1 ? "" : "s"} in the simple cohort. Complex or unrecorded settlements are excluded from these totals.`
                 : "No simple-cohort realized data yet. Every settled loan is either missing a recorded amount or flagged complex."}>
        {!hasRealized && (
          <div className="rounded-md bg-[#FFFBEB] border border-[#FCD34D] p-4 mb-4 text-xs text-[#78350F] flex items-start gap-2" data-testid="realized-empty-notice">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Realized totals are 0 by design.</div>
              <div className="mt-1">All {cnt.settled_closed} settled/closed loans are currently classified as unresolved (missing recorded amount or complex economics). Enter settlement amounts through the existing loan-detail workflow to populate this section — no data migration required.</div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={Banknote}   label="Recorded settlements"             value={fmtMoney(r.recorded_settlement_total)} testid="kpi-recorded-settlement" />
          <Kpi icon={Banknote}   label="Principal returned"               value={fmtMoney(r.principal_returned)} testid="kpi-principal-returned" />
          <Kpi icon={TrendingUp} label="Realized funding revenue"         value={fmtMoney(r.realized_funding_revenue)} accent testid="kpi-realized-revenue" />
          <Kpi icon={AlertTriangle} label="Principal loss"                value={fmtMoney(r.principal_loss)} negative testid="kpi-principal-loss" />

          <Kpi icon={Activity}   label="Negotiated reductions"            value={fmtMoney(r.negotiated_reductions_total)}
                sub="Separate component — never subtracted from revenue" testid="kpi-negotiated-reductions" />
          <Kpi icon={TrendingUp} label="MOIC — simple cohort"             value={r.moic_simple_cohort != null ? `${r.moic_simple_cohort.toFixed(2)}×` : "—"}
                sub={r.deployed_in_simple_cohort != null ? `On ${fmtMoney(r.deployed_in_simple_cohort)} deployed` : ""}
                testid="kpi-moic" />
        </div>
        <div className="mt-3 text-[11px] text-[#6B7280]">
          Notes:
          <ul className="list-disc pl-4 mt-1 space-y-0.5">
            {(r.notes || []).map((n, i) => <li key={i}>{n}</li>)}
          </ul>
        </div>
      </Section>

      <div className="mt-6 text-[11px] text-[#9CA3AF]" data-testid="finance-portfolio-as-of-label">
        Snapshot generated for as_of = {formatDate(data.as_of)} · {cnt.loans_total} loans considered
      </div>
    </div>
  );
}

const Section = ({ title, subtitle, category, children }) => {
  const meta = CATEGORY[category];
  return (
    <section className="mb-8">
      <div className="flex items-baseline gap-3 mb-3">
        <h2 className="font-display text-xl font-semibold text-[#0A2540]">{title}</h2>
        <CategoryChip category={category} />
      </div>
      {subtitle && <div className="text-xs text-[#6B7280] mb-3">{subtitle}</div>}
      <div className="rounded-lg border p-5" style={{ borderColor: meta.border, backgroundColor: meta.bg + "40" }}>
        {children}
      </div>
    </section>
  );
};

const CategoryChip = ({ category }) => {
  const m = CATEGORY[category] || CATEGORY.contractual;
  return (
    <span className="inline-block text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full"
          style={{ color: m.color, backgroundColor: m.bg, border: `1px solid ${m.border}` }}
          data-testid={`category-chip-${category}`}>
      {m.label}
    </span>
  );
};

const Kpi = ({ icon: Icon, label, value, sub, accent, negative, testid }) => (
  <div className="rounded-md border border-[#E5E7EB] bg-white p-3.5" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B7280] mb-1.5">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </div>
    <div className={`text-2xl font-semibold tabular-nums ${accent ? "text-[#0066FF]" : negative ? "text-[#991B1B]" : "text-[#0A2540]"}`}>{value}</div>
    {sub && <div className="text-[10px] text-[#9CA3AF] mt-1">{sub}</div>}
  </div>
);

const CountCard = ({ label, value, testid, accent, warn }) => (
  <div className="rounded-md border p-3 bg-white"
       style={{ borderColor: warn && value > 0 ? "#FCD34D" : "#E5E7EB",
                backgroundColor: warn && value > 0 ? "#FFFBEB" : "white" }}
       data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B7280]">{label}</div>
    <div className={`text-2xl font-semibold tabular-nums mt-1 ${accent ? "text-[#0066FF]" : warn && value > 0 ? "text-[#92400E]" : "text-[#0A2540]"}`}>{fmtNum(value)}</div>
  </div>
);

const DataQualityCard = ({ dq }) => {
  const rows = [
    {
      key: "class-c",
      label: `${dq.class_c.count} legacy Class-C loans excluded from realized analytics`,
      sub: `Principal exposure: ${fmtMoney(dq.class_c.principal_exposure)} · never estimated, never $0`,
      testid: "dq-class-c",
    },
    {
      key: "placeholder",
      label: `${dq.unreliable_dates.placeholder_settlement_date_count} realized loans with placeholder settlement dates`,
      sub: "settlement_date == funding_date — excluded from duration & IRR",
      testid: "dq-placeholder",
    },
    {
      key: "negative",
      label: `${dq.unreliable_dates.negative_duration_count} negative-duration record${dq.unreliable_dates.negative_duration_count === 1 ? "" : "s"} flagged`,
      sub: dq.unreliable_dates.negative_duration_loan_ids.length
        ? `Loan${dq.unreliable_dates.negative_duration_loan_ids.length === 1 ? "" : "s"}: ${dq.unreliable_dates.negative_duration_loan_ids.join(", ")}`
        : "None",
      testid: "dq-negative",
    },
    {
      key: "pending",
      label: `${dq.pending_final_verification.count} metrics pending final production tie-out`,
      sub: dq.pending_final_verification.metrics.map(m => m.label).join(" · "),
      testid: "dq-pending",
    },
  ];

  return (
    <section className="mb-8" data-testid="data-quality-card">
      <div className="flex items-baseline gap-3 mb-3">
        <h2 className="font-display text-xl font-semibold text-[#0A2540]">Data Quality</h2>
        <span className="text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full"
              style={{ color: "#78350F", backgroundColor: "#FFFBEB", border: "1px solid #FCD34D" }}>
          Disclosure
        </span>
      </div>
      <div className="text-xs text-[#6B7280] mb-3">
        Permanent audit disclosures. See Settlement Diagnostic for per-loan detail. Legacy imported records are not repaired.
      </div>
      <div className="rounded-lg border p-4 bg-white" style={{ borderColor: "#FCD34D" }}>
        <ul className="space-y-3">
          {rows.map(r => (
            <li key={r.key} className="flex items-start gap-3" data-testid={r.testid}>
              <ShieldCheck className="w-4 h-4 mt-0.5 text-[#92400E] flex-shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-medium text-[#0A2540]">{r.label}</div>
                <div className="text-[11px] text-[#6B7280] mt-0.5">{r.sub}</div>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-4 pt-3 border-t border-[#F3F4F6] flex items-center justify-between">
          <div className="text-[11px] text-[#6B7280]">
            Denominator disclosure: {dq.reliable_realized_cohort.denominator_disclosure}
          </div>
          <Link to="/admin/finance/diagnostic"
                data-testid="dq-diagnostic-link"
                className="text-xs font-medium text-[#0066FF] hover:underline flex items-center gap-1">
            Open Settlement Diagnostic <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </section>
  );
};
